/**
 * Anillo segmentado del nodo + mini-anillo de reps (fase 17.C, portado de
 * `program-ring.jsx` del diseño v9).
 *
 * - `SegmentedRing`: UN segmento por sub-unidad alrededor del nodo. Los
 *   segmentos se ponen lima al completarse. Un solo SVG centrado → sin la
 *   desalineación del arco viejo (`.pg-node-arc`, que reemplaza).
 * - `RepRing`: mini-anillo de reps para el tracker en vivo (fase 17.B).
 */

export type SegState = 'mastered' | 'progress' | 'none';

function ringPolar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

export function ringArc(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const [x1, y1] = ringPolar(cx, cy, r, startDeg);
  const [x2, y2] = ringPolar(cx, cy, r, endDeg);
  const large = (endDeg - startDeg) % 360 > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

interface SegmentedRingProps {
  size?: number;
  /** Estado por sub-unidad. `['none']` para units legacy (1 segmento). */
  segStates?: ReadonlyArray<SegState>;
  /** Color de realce exterior (naranja si "próxima sugerida"), o null. */
  accent?: string | null;
  animate?: boolean;
}

export function SegmentedRing({
  size = 56,
  segStates = ['none'],
  accent = null,
  animate = true,
}: SegmentedRingProps) {
  const n = Math.max(1, segStates.length);
  const pad = 7;
  const sw = n >= 7 ? 3 : n >= 5 ? 3.4 : 4;
  const box = size + pad * 2 + sw * 2;
  const cx = box / 2;
  const cy = box / 2;
  const r = size / 2 + pad;
  const gap = n === 1 ? 0 : n <= 3 ? 15 : n <= 5 ? 10 : n <= 6 ? 8 : 6;
  const slot = 360 / n;

  return (
    <svg
      className="pg-ring-svg"
      width={box}
      height={box}
      viewBox={`0 0 ${box} ${box}`}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%,-50%)',
        pointerEvents: 'none',
        overflow: 'visible',
      }}
      aria-hidden="true"
    >
      {Array.from({ length: n }).map((_, i) => {
        const start = i * slot + (n === 1 ? 0 : gap / 2);
        const end = (i + 1) * slot - (n === 1 ? 0 : gap / 2);
        const st = segStates[i] ?? 'none';
        const d =
          n === 1
            ? `M ${ringPolar(cx, cy, r, 0.01)[0]} ${ringPolar(cx, cy, r, 0.01)[1]} A ${r} ${r} 0 1 1 ${ringPolar(cx, cy, r, 359.99)[0]} ${ringPolar(cx, cy, r, 359.99)[1]}`
            : ringArc(cx, cy, r, start, end);
        const mastered = st === 'mastered';
        const progress = st === 'progress';
        const stroke = mastered || progress ? 'var(--lime)' : 'rgba(233,234,242,0.20)';
        const op = progress ? 0.6 : 1;
        const dash = !mastered && !progress ? '1 5' : undefined;
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
            opacity={op}
            {...(dash ? { strokeDasharray: dash } : {})}
            className={progress ? 'pg-seg-progress' : ''}
            style={{ transition: animate ? 'stroke 0.5s ease, opacity 0.5s ease' : 'none' }}
          />
        );
      })}
      {accent && (
        <circle
          cx={cx}
          cy={cy}
          r={r + sw + 2.5}
          fill="none"
          stroke={accent}
          strokeWidth="1.5"
          opacity="0.5"
          strokeDasharray="2 4"
        />
      )}
    </svg>
  );
}

interface RepRingProps {
  size?: number;
  done?: number;
  target?: number;
  color?: string;
  complete?: boolean;
  flash?: boolean;
}

/** Mini-anillo de reps (tracker en vivo, fase 17.B): done/target ticks. */
export function RepRing({
  size = 26,
  done = 0,
  target = 3,
  color = 'var(--prog)',
  complete = false,
  flash = false,
}: RepRingProps) {
  const n = Math.max(1, target);
  const sw = size <= 22 ? 2.6 : 3;
  const pad = sw + 1;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - pad;
  const gap = n <= 2 ? 26 : n <= 3 ? 20 : 14;
  const slot = 360 / n;
  return (
    <span
      className={`rep-ring${complete ? ' is-complete' : ''}${flash ? ' is-flash' : ''}`}
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
        {Array.from({ length: n }).map((_, i) => {
          const start = i * slot + gap / 2;
          const end = (i + 1) * slot - gap / 2;
          const filled = i < done;
          return (
            <path
              key={i}
              d={ringArc(cx, cy, r, start, end)}
              fill="none"
              stroke={filled ? color : 'rgba(233,234,242,0.18)'}
              strokeWidth={sw}
              strokeLinecap="round"
              style={{ transition: 'stroke 0.45s cubic-bezier(.2,.8,.2,1)' }}
            />
          );
        })}
      </svg>
      <span
        className="rep-ring-core"
        style={{
          position: 'absolute',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: size <= 22 ? 8.5 : 9.5,
          fontWeight: 600,
          color: complete ? color : 'var(--text-muted)',
          lineHeight: 1,
        }}
      >
        {complete ? (
          <svg
            viewBox="0 0 24 24"
            width={size * 0.42}
            height={size * 0.42}
            fill="none"
            stroke={color}
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12 L 10 18 L 20 6" />
          </svg>
        ) : (
          <span>
            {done}
            <span style={{ opacity: 0.5 }}>/{target}</span>
          </span>
        )}
      </span>
    </span>
  );
}
